/** Item master, variants, barcodes and item groups. Mirrors `core/api/routes/items.py`. */
import { z } from 'zod';
import { barcodeTypeSchema, itemTypeSchema, lifecycleStatusSchema, trackingPolicySchema, valuationMethodSchema, } from './enums.js';
import { decimalIn, decimalOut, readableSchema, uuidSchema } from './primitives.js';
// ------------------------------------------------------------------ item groups
export const itemGroupSchema = readableSchema.extend({
    code: z.string(),
    name: z.string(),
    description: z.string().nullable().default(null),
    parent_id: uuidSchema.nullable().default(null),
    is_active: z.boolean(),
});
export const itemGroupCreateSchema = z.object({
    code: z.string().min(1).max(40),
    name: z.string().min(1).max(200),
    description: z.string().nullish(),
    parent_id: uuidSchema.nullish(),
    is_active: z.boolean().default(true),
});
export const itemGroupPatchSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().nullable().optional(),
    parent_id: uuidSchema.nullable().optional(),
    is_active: z.boolean().optional(),
});
// ------------------------------------------------------------------------ items
/**
 * An item as the server returns it.
 *
 * Note what is NOT here, and note it deliberately: no `current_stock`, no `average_cost`,
 * no `reserved_stock`, no `sales_price`. Slice 2 refused all four. Stock figures are
 * ledger projections read from `/v1/stock/balances` and `/v1/stock/availability`; selling
 * prices live in price lists or a host overlay (D-16). ICRM carries exactly those dead
 * columns on `crm_products` and nothing writes them — a skin that renders an item's
 * on-hand from the item row is reproducing that bug.
 */
export const itemSchema = readableSchema.extend({
    item_code: z.string(),
    name: z.string(),
    description: z.string().nullable().default(null),
    item_type: itemTypeSchema,
    /** Generated by the database from `item_type`. Never sent on create or patch. */
    is_stock_tracked: z.boolean(),
    item_group_id: uuidSchema.nullable().default(null),
    brand: z.string().nullable().default(null),
    manufacturer_name: z.string().nullable().default(null),
    is_bundle: z.boolean(),
    base_uom_id: uuidSchema,
    purchase_uom_id: uuidSchema.nullable().default(null),
    sales_uom_id: uuidSchema.nullable().default(null),
    hsn_code: z.string().nullable().default(null),
    sac_code: z.string().nullable().default(null),
    gst_rate: decimalOut,
    tax_applicable: z.boolean(),
    valuation_method: valuationMethodSchema,
    /** A per-unit rate: `Numeric(20, 4)`, not paise. See invariant 5. */
    standard_cost: decimalOut,
    tracking_policy: trackingPolicySchema,
    shelf_life_days: z.number().int().nullable().default(null),
    requires_expiry_date: z.boolean(),
    reorder_level: decimalOut,
    min_stock_level: decimalOut,
    max_stock_level: decimalOut,
    lead_time_days: z.number().int().nullable().default(null),
    status: lifecycleStatusSchema,
});
export const itemCreateSchema = z.object({
    /** Omit to have the server generate `ITM-nnnnnn`. */
    item_code: z.string().max(40).nullish(),
    name: z.string().min(1).max(255),
    base_uom_id: uuidSchema,
    description: z.string().nullish(),
    item_type: itemTypeSchema.default('STOCK'),
    item_group_id: uuidSchema.nullish(),
    brand: z.string().max(100).nullish(),
    manufacturer_name: z.string().max(200).nullish(),
    is_bundle: z.boolean().default(false),
    purchase_uom_id: uuidSchema.nullish(),
    sales_uom_id: uuidSchema.nullish(),
    hsn_code: z.string().max(8).nullish(),
    sac_code: z.string().max(6).nullish(),
    gst_rate: decimalIn({ gte: '0', lte: '100', maxPlaces: 2, label: 'GST rate' }).default('0'),
    tax_applicable: z.boolean().default(true),
    valuation_method: valuationMethodSchema.default('WAC'),
    standard_cost: decimalIn({ gte: '0', label: 'standard cost' }).default('0'),
    tracking_policy: trackingPolicySchema.default('NONE'),
    shelf_life_days: z.number().int().min(0).nullish(),
    requires_expiry_date: z.boolean().default(false),
    reorder_level: decimalIn({ gte: '0', label: 'reorder level' }).default('0'),
    min_stock_level: decimalIn({ gte: '0', label: 'minimum stock level' }).default('0'),
    max_stock_level: decimalIn({ gte: '0', label: 'maximum stock level' }).default('0'),
    lead_time_days: z.number().int().min(0).nullish(),
});
/**
 * `item_code` and `valuation_method` are absent, and that is the contract rather than an
 * oversight: changing a valuation method after movements exist would silently reinterpret
 * every cost layer already posted. The server refuses both in the service layer too, so
 * this is defence in depth, not the control.
 */
export const itemPatchSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().nullable().optional(),
    item_group_id: uuidSchema.nullable().optional(),
    brand: z.string().max(100).nullable().optional(),
    manufacturer_name: z.string().max(200).nullable().optional(),
    purchase_uom_id: uuidSchema.nullable().optional(),
    sales_uom_id: uuidSchema.nullable().optional(),
    hsn_code: z.string().max(8).nullable().optional(),
    sac_code: z.string().max(6).nullable().optional(),
    gst_rate: decimalIn({ gte: '0', lte: '100', maxPlaces: 2, label: 'GST rate' }).optional(),
    tax_applicable: z.boolean().optional(),
    standard_cost: decimalIn({ gte: '0', label: 'standard cost' }).optional(),
    tracking_policy: trackingPolicySchema.optional(),
    shelf_life_days: z.number().int().min(0).nullable().optional(),
    requires_expiry_date: z.boolean().optional(),
    reorder_level: decimalIn({ gte: '0', label: 'reorder level' }).optional(),
    min_stock_level: decimalIn({ gte: '0', label: 'minimum stock level' }).optional(),
    max_stock_level: decimalIn({ gte: '0', label: 'maximum stock level' }).optional(),
    lead_time_days: z.number().int().min(0).nullable().optional(),
    status: lifecycleStatusSchema.optional(),
});
// --------------------------------------------------------------------- variants
export const itemVariantSchema = readableSchema.extend({
    item_id: uuidSchema,
    variant_code: z.string(),
    name: z.string(),
    attributes: z.record(z.unknown()),
    is_active: z.boolean(),
});
export const itemVariantCreateSchema = z.object({
    /** Filled in from the path by the client; the route overrides the body regardless. */
    item_id: uuidSchema.optional(),
    variant_code: z.string().min(1).max(60),
    name: z.string().min(1).max(255),
    attributes: z.record(z.unknown()).default({}),
    is_active: z.boolean().default(true),
});
// --------------------------------------------------------------------- barcodes
export const barcodeSchema = readableSchema.extend({
    item_id: uuidSchema,
    variant_id: uuidSchema.nullable().default(null),
    barcode: z.string(),
    barcode_type: barcodeTypeSchema,
    /** A carton barcode and a single barcode resolve to the same item and mean different
     *  quantities. A scan path that ignores this books one unit for a case. */
    uom_id: uuidSchema.nullable().default(null),
    is_primary: z.boolean(),
});
export const barcodeCreateSchema = z.object({
    item_id: uuidSchema,
    barcode: z.string().min(1).max(64),
    barcode_type: barcodeTypeSchema.default('INTERNAL'),
    variant_id: uuidSchema.nullish(),
    uom_id: uuidSchema.nullish(),
    is_primary: z.boolean().default(false),
});
//# sourceMappingURL=items.js.map